import React from 'react';
import styled from '@emotion/styled';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName } from '../../utils/lensDisplay';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

// ─── Shared Panel ───────────────────────────────────────────────────

const Panel = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 20px;
`;

const PanelTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

// ─── Heatmap ────────────────────────────────────────────────────────

const HeatmapGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 200px) repeat(${p => p.$cols}, 1fr) 60px;
  gap: 2px;
  font-size: 0.78rem;
`;

const HeaderCell = styled.div`
  padding: 8px 6px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowLabel = styled.div`
  padding: 8px 10px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeatCell = styled.div`
  padding: 6px 4px;
  text-align: center;
  border-radius: 4px;
  background: ${p => p.$intensity > 0
    ? `rgba(255, 255, 255, ${Math.min(0.03 + p.$intensity * 0.04, 0.25)})`
    : 'rgba(255, 255, 255, 0.01)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 32px;
  transition: background 0.15s ease;
`;

const CellCount = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${p => p.$color};
  opacity: ${p => p.$value > 0 ? 1 : 0.2};
`;

const TotalCell = styled.div`
  padding: 6px 4px;
  text-align: center;
  font-weight: 700;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(255, 255, 255, 0.06);
`;

const TotalHeaderCell = styled(HeaderCell)`
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.4);
`;

const FooterRow = styled.div`
  display: contents;
`;

const FooterLabel = styled.div`
  padding: 8px 10px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
`;

const FooterCell = styled.div`
  padding: 6px 4px;
  text-align: center;
  font-weight: 700;
  font-size: 0.78rem;
  color: ${p => p.$color || 'rgba(255, 255, 255, 0.5)'};
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ─── Chart ──────────────────────────────────────────────────────────

const ChartWrapper = styled.div`
  width: 100%;
  height: 280px;
`;

const CustomTooltipWrapper = styled.div`
  background: rgba(10, 10, 10, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #fff;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const TooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
`;

function CustomTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  return (
    <CustomTooltipWrapper>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{payload[0]?.payload?.fullName || label}</div>
      {payload.map((entry, idx) => (
        <TooltipRow key={idx}>
          <TooltipDot $color={entry.color} />
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            {entry.name === 'obstacles' ? t('lenses.obstacles') : t('lenses.opportunities')}:
          </span>
          <span style={{ fontWeight: 600 }}>{entry.value}</span>
        </TooltipRow>
      ))}
    </CustomTooltipWrapper>
  );
}

/**
 * DashboardCharts — Heatmap grid + recharts BarChart.
 */
export default function DashboardCharts({
  matrixData = {},
  lensDistribution = [],
  sortedLenses = [],
  pivotGroups = [],
}) {
  const { t, language } = useTranslation();
  const lensCount = sortedLenses.length;

  // Column net scores for heatmap footer (opportunities - obstacles)
  const colScores = sortedLenses.map(lens => {
    let obstacles = 0;
    let opportunities = 0;
    Object.values(matrixData).forEach(pivotRow => {
      const cell = pivotRow[lens.id];
      if (cell) {
        obstacles += cell.obstacles;
        opportunities += cell.opportunities;
      }
    });
    return opportunities - obstacles;
  });
  const grandScore = colScores.reduce((s, v) => s + v, 0);

  return (
    <>
      {/* Section 2: Pivot × Lens Heatmap */}
      <Panel>
        <PanelTitle>{t('lenses.matrixTitle')}</PanelTitle>
        <div style={{ overflowX: 'auto' }}>
          <HeatmapGrid $cols={lensCount}>
            {/* Header row */}
            <HeaderCell />
            {sortedLenses.map(lens => {
              // name_en when the workshop runs in English — see utils/lensDisplay
              const label = lensDisplayName(lens, language);
              return (
                <HeaderCell key={lens.id} title={label}>
                  {label.length > 10 ? label.substring(0, 10) + '…' : label}
                </HeaderCell>
              );
            })}
            <TotalHeaderCell>{t('lenses.totalShort')}</TotalHeaderCell>

            {/* Data rows */}
            {pivotGroups.map(pg => {
              const rowTotal = pg.obstacles + pg.opportunities;
              return (
                <React.Fragment key={pg.pivotId}>
                  <RowLabel title={pg.title}>
                    {pg.title.length > 24 ? pg.title.substring(0, 24) + '…' : pg.title}
                  </RowLabel>
                  {sortedLenses.map(lens => {
                    const cell = matrixData[pg.pivotId]?.[lens.id] || { obstacles: 0, opportunities: 0 };
                    const total = cell.obstacles + cell.opportunities;
                    const emptyCell = cell.obstacles === 0 && cell.opportunities === 0;
                    return (
                      <HeatCell key={lens.id} $intensity={total}>
                        {emptyCell ? (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>—</span>
                        ) : (
                          <>
                            <CellCount $color={COLORS.obstacle} $value={cell.obstacles}>
                              {cell.obstacles}
                            </CellCount>
                            <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.65rem' }}>/</span>
                            <CellCount $color={COLORS.opportunity} $value={cell.opportunities}>
                              {cell.opportunities}
                            </CellCount>
                          </>
                        )}
                      </HeatCell>
                    );
                  })}
                  <TotalCell>{rowTotal}</TotalCell>
                </React.Fragment>
              );
            })}

            {/* Footer row (net score: opportunities - obstacles) */}
            <FooterRow>
              <FooterLabel>{t('lenses.total')}</FooterLabel>
              {colScores.map((score, i) => (
                <FooterCell
                  key={i}
                  $color={score > 0 ? COLORS.opportunity : score < 0 ? COLORS.obstacle : 'rgba(255,255,255,0.4)'}
                >
                  {score > 0 ? '+' : ''}{score}
                </FooterCell>
              ))}
              <FooterCell
                style={{ fontWeight: 800 }}
                $color={grandScore > 0 ? COLORS.opportunity : grandScore < 0 ? COLORS.obstacle : 'rgba(255,255,255,0.4)'}
              >
                {grandScore > 0 ? '+' : ''}{grandScore}
              </FooterCell>
            </FooterRow>
          </HeatmapGrid>
        </div>
      </Panel>

      {/* Section 3: Lens Distribution Chart */}
      <Panel>
        <PanelTitle>{t('lenses.lensDistribution')}</PanelTitle>
        <ChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={lensDistribution} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="name"
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Legend
                formatter={(value) => value === 'obstacles' ? t('lenses.obstacles') : t('lenses.opportunities')}
                wrapperStyle={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)' }}
              />
              <Bar dataKey="obstacles" fill={COLORS.obstacle} radius={[4, 4, 0, 0]} />
              <Bar dataKey="opportunities" fill={COLORS.opportunity} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </Panel>
    </>
  );
}
